import { useEffect, useState } from "react"

// Sticky "jump to section" bar for the long beneficiary detail page.
//
// The detail page renders 10+ sections, most of them conditional: some are
// gated by user.settings.* flags, others self-hide when they have no data
// (Blood Pressure, Watch Health, Locate Now). Rather than duplicate all those
// visibility conditions here, the nav scans the live DOM for the known section
// ids and shows a chip only for the sections actually present. A MutationObserver
// re-scans as async panels mount/unmount so the bar stays in sync.
//
// Keep ids/order in sync with the section root `id`s in DashboardDetails.tsx,
// LocationReport, LocateNow, *Report components, and ChartCard (`chart_<sensor>`).
const SECTION_LABELS: { id: string; label: string }[] = [
    { id: 'score', label: 'Wellness' },
    { id: 'room', label: 'Last Seen' },
    { id: 'location_report', label: 'Location' },
    { id: 'locate_now', label: 'Locate' },
    { id: 'sleep_report', label: 'In Bed' },
    { id: 'blood_pressure_report', label: 'Blood Pressure' },
    { id: 'watch_health_report', label: 'Watch Health' },
    { id: 'walks_report', label: 'Walks' },
    { id: 'activity_report', label: 'Activity' },
    { id: 'chart_radar', label: 'Radar' },
    { id: 'chart_light', label: 'Light' },
    { id: 'chart_co2', label: 'Air Quality' },
    { id: 'chart_temperature', label: 'Temperature' },
    { id: 'chart_humidity', label: 'Humidity' },
    { id: 'chart_pressure', label: 'Air Pressure' },
    { id: 'other_activities', label: 'Other' },
]

export function SectionNav() {
    const [presentIds, setPresentIds] = useState<string[]>([])

    useEffect(() => {
        const scan = () => {
            const ids = SECTION_LABELS.filter(s => document.getElementById(s.id)).map(s => s.id)
            // Skip the state update when nothing changed so the observer firing on
            // every unrelated mutation (chart renders, the 1s clock tick) doesn't
            // churn renders.
            setPresentIds(prev =>
                prev.length === ids.length && prev.every((p, i) => p === ids[i]) ? prev : ids
            )
        }
        scan()
        const observer = new MutationObserver(scan)
        observer.observe(document.body, { childList: true, subtree: true })
        return () => observer.disconnect()
    }, [])

    const items = SECTION_LABELS.filter(s => presentIds.includes(s.id))

    // Not worth a nav bar for one or two sections.
    if (items.length < 3) return null

    const jump = (id: string) =>
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

    return (
        <div className="sticky top-0 z-30 -mx-4 border-b border-gray-200 bg-[#e9eefb]/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-[#e9eefb]/80">
            <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {items.map(s => (
                    <button
                        key={s.id}
                        type="button"
                        onClick={() => jump(s.id)}
                        className="whitespace-nowrap rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:border-blue-300 hover:bg-blue-50"
                    >
                        {s.label}
                    </button>
                ))}
            </div>
        </div>
    )
}
